// ============================================================
// [P21] Santé de l'instance — monitoring live des services (db/redis/minio/
// livekit) + version + uptime. Lit /healthz (public) en polling 10 s.
// ============================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE } from '../apiBase';

type CheckState = 'ok' | 'degraded' | 'down';
interface HealthResult {
  status: CheckState;
  version: string;
  uptimeSeconds: number;
  checks: Record<string, CheckState>;
}

const COLOR: Record<CheckState, string> = {
  ok: 'var(--color-success)',
  degraded: 'var(--color-warning, #d29922)',
  down: 'var(--color-error)',
};
const LABEL: Record<CheckState, string> = { ok: 'OK', degraded: 'Dégradé', down: 'HS' };

function fmtUptime(s: number): string {
  if (!Number.isFinite(s) || s < 0) return '—';
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}j ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${Math.floor(s % 60)}s`;
}

export function HealthPage(): JSX.Element {
  const [data, setData] = useState<HealthResult | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [lastAt, setLastAt] = useState<Date | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/healthz`, { headers: { accept: 'application/json' } });
      const j = (await r.json()) as HealthResult;
      setData(j);
      setErr(null);
      setLastAt(new Date());
    } catch (e) {
      setErr((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
    timer.current = setInterval(() => void load(), 10000);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [load]);

  const overall: CheckState = data?.status ?? (err ? 'down' : 'degraded');

  return (
    <div>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
      >
        <h1 style={{ margin: 0 }}>Santé de l'instance</h1>
        <button
          onClick={() => void load()}
          style={{
            padding: '6px 12px',
            background: 'transparent',
            color: 'var(--color-text)',
            border: '1px solid var(--color-border)',
            borderRadius: 6,
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          ↻ Rafraîchir
        </button>
      </div>
      <p style={{ color: 'var(--color-text-muted)', fontSize: 13, marginTop: 4 }}>
        Lecture de <code>/healthz</code> toutes les 10 s ·{' '}
        {lastAt ? `MAJ ${lastAt.toLocaleTimeString('fr-FR')}` : '…'}
      </p>

      {err && (
        <div
          style={{
            padding: '10px 14px',
            background: 'rgba(248,81,73,.15)',
            color: 'var(--color-error)',
            borderRadius: 8,
            fontSize: 14,
            marginBottom: 16,
          }}
        >
          API injoignable : {err}
        </div>
      )}

      {/* Bandeau global */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: '16px 20px',
          background: 'var(--color-surface)',
          border: `1px solid ${COLOR[overall]}`,
          borderRadius: 10,
          marginBottom: 20,
        }}
      >
        <span
          style={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            background: COLOR[overall],
            flexShrink: 0,
          }}
        />
        <div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>
            {overall === 'ok' ? 'Tous les services opérationnels' : `État : ${LABEL[overall]}`}
          </div>
          <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
            version <strong>{data?.version ?? '—'}</strong> · uptime{' '}
            <strong>{data ? fmtUptime(data.uptimeSeconds) : '—'}</strong>
          </div>
        </div>
      </div>

      {/* Cartes par service */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 14,
        }}
      >
        {data &&
          Object.entries(data.checks).map(([name, state]) => (
            <div
              key={name}
              style={{
                padding: '14px 16px',
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: 10,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  textTransform: 'uppercase',
                }}
              >
                <span
                  style={{ width: 10, height: 10, borderRadius: '50%', background: COLOR[state] }}
                />
                <span style={{ fontSize: 13, fontWeight: 600 }}>{name}</span>
              </div>
              <div style={{ marginTop: 8, fontSize: 20, fontWeight: 700, color: COLOR[state] }}>
                {LABEL[state]}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
